import type {
  Block,
  BlockAudio,
  BlockCloze,
  BlockFile,
  BlockMedia,
  QuestionBlock,
} from '@w/content-blocks'
import type { ViewDelta } from '@w/crdt'
import { getAffectedBlocks, classifyBlockDelta, type AffectedBlocks } from './view-delta'
import { pageDocument } from './doc-bindings-for-page'

export type QuestionCdcBlock = QuestionBlock | BlockCloze
export type MediaCdcBlock = BlockMedia | BlockAudio | BlockFile
export type PageRelationCdcState = Map<
  string,
  { parentCount: number; linkCount: number }
>
export type PageCdcPage = {
  id: string
  workspaceId: string
  parentId: string | null
  title: string
  content: { children: Block[] }
  embeddingDirty: boolean
  deletedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export type PageCdcChange =
  | { type: 'page.updated'; page: PageCdcPage }
  | {
      type: 'pageRelation.upserted'
      relation: {
        workspaceId: string
        pageSource: string
        pageLinked: string
        relation: string | null
        isParent: boolean | null
      }
    }
  | {
      type: 'pageRelation.deleted'
      relation: {
        workspaceId: string
        pageSource: string
        pageLinked: string
      }
    }
  | {
      type: 'question.inserted'
      block: QuestionCdcBlock
      /**
       * Sitting-order kind for lexicon-minted cards, recorded once at mint
       * time (adoptEntry) — never inferred from the block. Absent/undefined
       * for ordinary editor-authored question blocks, which persist as
       * `cardKind = null` (note card).
       */
      cardKind?: string
    }
  | {
      type: 'question.updated'
      before: QuestionCdcBlock
      after: QuestionCdcBlock
    }
  | {
      type: 'question.deleted'
      block: QuestionCdcBlock
      deletedContent: Block[]
    }
  | { type: 'media.restored'; block: MediaCdcBlock }
  | { type: 'media.deleted'; block: MediaCdcBlock }

export function getQuestionCdcChanges(delta: ViewDelta): PageCdcChange[] {
  const args = { affected: getAffectedBlocks(delta),
    before: pageDocument({ toJSON: () => delta.before }).content.children as unknown as Block[],
    after: pageDocument({ toJSON: () => delta.after }).content.children as unknown as Block[] }
  const changes: PageCdcChange[] = []

  const insertedBlocks = getInsertedQuestionBlocks(args.affected)
  const insertedQids = new Set(insertedBlocks.map((block) => block.qid))

  for (const block of insertedBlocks) {
    changes.push({ type: 'question.inserted', block })
  }
  if (args.before && args.after) {
    changes.push(...getQuestionUpdates(delta))
  }
  for (const block of getDeletedQuestionBlocks(args.affected)) {
    // Editing a question re-mints its block id, so the same qid shows up as
    // both deleted (old id) and inserted (new id). That is an in-place edit,
    // not a deletion — emitting the delete would (re)mark the card deleted.
    if (insertedQids.has(block.qid)) continue
    changes.push({
      type: 'question.deleted',
      block,
      deletedContent: getDeletedQuestionContent(args.before, block),
    })
  }

  return changes
}

function getDeletedQuestionContent(
  before: Block[] | undefined,
  block: QuestionCdcBlock,
): Block[] {
  if (!before) return [block]
  const container = findDeletedQuestionContainer(before, block.id)
  return [container ?? block]
}

function findDeletedQuestionContainer(
  blocks: Block[],
  targetId: string,
): Block | null {
  for (const block of blocks) {
    const container = findDeletedQuestionContainerInBlock(block, targetId)
    if (container) return container
  }
  return null
}

function findDeletedQuestionContainerInBlock(
  block: Block,
  targetId: string,
): Block | null {
  if (block.id === targetId) return block

  for (const nested of getNestedBlocks(block)) {
    const child = findDeletedQuestionContainerInValue(nested, targetId)
    if (!child) continue
    return child.id === targetId ? block : child
  }

  return null
}

function findDeletedQuestionContainerInValue(
  value: unknown,
  targetId: string,
): Block | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const block = findDeletedQuestionContainerInValue(item, targetId)
      if (block) return block
    }
    return null
  }

  if (!isBlock(value)) return null
  return findDeletedQuestionContainerInBlock(value, targetId)
}

export function getInsertedQuestionBlocks(affected: {
  inserted: AffectedBlocks
}): QuestionCdcBlock[] {
  return [
    ...(affected.inserted.question ?? []),
    ...(affected.inserted.cloze ?? []),
  ]
}

export function getDeletedQuestionBlocks(affected: {
  deleted: AffectedBlocks
}): QuestionCdcBlock[] {
  return [
    ...(affected.deleted.question ?? []),
    ...(affected.deleted.cloze ?? []),
  ]
}

export function getMediaCdcChanges(delta: ViewDelta): PageCdcChange[] {
  const affected = getAffectedBlocks(delta)
  return [
    ...getInsertedMediaBlocks(affected).map((block) => ({
      type: 'media.restored' as const,
      block,
    })),
    ...getDeletedMediaBlocks(affected).map((block) => ({
      type: 'media.deleted' as const,
      block,
    })),
  ]
}

export function getPageRelationCdcChanges(input: {
  delta: ViewDelta
  workspaceId: string
  pageId: string
  state?: PageRelationCdcState
}): PageCdcChange[] {
  const args = { ...input, affected: getAffectedBlocks(input.delta) }
  const changes: PageCdcChange[] = []
  const state = args.state ?? new Map()

  for (const block of args.affected.inserted.page ?? []) {
    if (block.pageId === args.pageId) continue
    updateRelationState(state, block.pageId, 'parentCount', 1)
    changes.push(upsertRelation(args, block.pageId, state))
  }
  for (const block of args.affected.inserted.pageLink ?? []) {
    if (block.pageId === args.pageId) continue
    updateRelationState(state, block.pageId, 'linkCount', 1)
    changes.push(upsertRelation(args, block.pageId, state))
  }
  for (const block of args.affected.deleted.page ?? []) {
    if (block.pageId === args.pageId) continue
    updateRelationState(state, block.pageId, 'parentCount', -1)
    changes.push(nextRelationChange(args, block.pageId, state))
  }
  for (const block of args.affected.deleted.pageLink ?? []) {
    if (block.pageId === args.pageId) continue
    updateRelationState(state, block.pageId, 'linkCount', -1)
    changes.push(nextRelationChange(args, block.pageId, state))
  }

  return changes
}

function updateRelationState(
  state: PageRelationCdcState,
  pageLinked: string,
  key: 'parentCount' | 'linkCount',
  delta: 1 | -1,
) {
  const current = state.get(pageLinked) ?? { parentCount: 0, linkCount: 0 }
  current[key] = Math.max(0, current[key] + delta)
  state.set(pageLinked, current)
}

function nextRelationChange(
  args: { workspaceId: string; pageId: string },
  pageLinked: string,
  state: PageRelationCdcState,
): PageCdcChange {
  const current = state.get(pageLinked)
  if (current && current.parentCount + current.linkCount > 0) {
    return upsertRelation(args, pageLinked, state)
  }
  return {
    type: 'pageRelation.deleted',
    relation: {
      workspaceId: args.workspaceId,
      pageSource: args.pageId,
      pageLinked,
    },
  }
}

function upsertRelation(
  args: { workspaceId: string; pageId: string },
  pageLinked: string,
  state: PageRelationCdcState,
): PageCdcChange {
  return {
    type: 'pageRelation.upserted',
    relation: {
      workspaceId: args.workspaceId,
      pageSource: args.pageId,
      pageLinked,
      relation: null,
      isParent: (state.get(pageLinked)?.parentCount ?? 0) > 0,
    },
  }
}

function getInsertedMediaBlocks(affected: {
  inserted: AffectedBlocks
}): MediaCdcBlock[] {
  return [
    ...(affected.inserted.audio ?? []),
    ...(affected.inserted.img ?? []),
    ...(affected.inserted.file ?? []),
    ...(affected.inserted.video ?? []),
  ]
}

function getDeletedMediaBlocks(affected: {
  deleted: AffectedBlocks
}): MediaCdcBlock[] {
  return [
    ...(affected.deleted.audio ?? []),
    ...(affected.deleted.img ?? []),
    ...(affected.deleted.file ?? []),
    ...(affected.deleted.video ?? []),
  ]
}

function getQuestionUpdates(delta: ViewDelta): PageCdcChange[] {
  const changes: PageCdcChange[] = []
  for (const change of classifyBlockDelta(delta).updated) {
    const before = change.before.block as unknown as Block
    const after = change.after.block as unknown as Block
    if ((before.type !== 'question' && before.type !== 'cloze') ||
      (after.type !== 'question' && after.type !== 'cloze') || before.qid !== after.qid ||
      before.fractionalIndex === after.fractionalIndex) continue
    changes.push({ type: 'question.updated', before, after })
  }
  return changes
}




function getNestedBlocks(block: Block): unknown[] {
  const nested: unknown[] = []
  if ('children' in block) nested.push(block.children)
  if (block.type === 'question') {
    nested.push(block.question, block.explanation)
    if (block.subtype === 'sc' || block.subtype === 'mc') {
      nested.push(block.opts)
    }
  }
  return nested
}

function isBlock(value: unknown): value is Block {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof value.type === 'string'
  )
}
